import { Link } from "react-router-dom";
import { motion } from "framer-motion";

type Crumb = { label: string; to?: string };
type Props = {
  eyebrow?: string;
  title: string;
  subtitle?: string;
  breadcrumbs?: Crumb[];
};

const ease = [0.22, 1, 0.36, 1] as const;

export function PageHeader({ eyebrow, title, subtitle, breadcrumbs }: Props) {
  return (
    <section className="relative isolate overflow-hidden">
      <div className="absolute inset-0 -z-10 gradient-hero" aria-hidden="true" />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -top-32 -right-20 h-80 w-80 rounded-full bg-brand-300/30 blur-3xl -z-10"
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -bottom-32 -left-20 h-80 w-80 rounded-full bg-accent-400/20 blur-3xl -z-10"
      />

      <div className="container-page py-16 sm:py-24">
        {breadcrumbs && (
          <motion.nav
            aria-label="Fil d'Ariane"
            className="mb-5 text-sm"
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, ease }}
          >
            <ol className="flex flex-wrap items-center gap-1.5 text-ink-muted">
              {breadcrumbs.map((c, i) => (
                <li key={i} className="flex items-center gap-1.5">
                  {c.to ? (
                    <Link to={c.to} className="hover:text-brand-700">
                      {c.label}
                    </Link>
                  ) : (
                    <span className="text-brand-900">{c.label}</span>
                  )}
                  {i < breadcrumbs.length - 1 && <span className="text-slate-300">/</span>}
                </li>
              ))}
            </ol>
          </motion.nav>
        )}
        {eyebrow && (
          <motion.span
            className="eyebrow"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.05, ease }}
          >
            {eyebrow}
          </motion.span>
        )}
        <motion.h1
          className="mt-4 max-w-3xl text-balance"
          initial={{ opacity: 0, y: 20, filter: "blur(6px)" }}
          animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
          transition={{ duration: 0.6, delay: 0.1, ease }}
        >
          {title}
        </motion.h1>
        {subtitle && (
          <motion.p
            className="mt-5 max-w-2xl text-lg text-ink-muted"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2, ease }}
          >
            {subtitle}
          </motion.p>
        )}
      </div>
    </section>
  );
}
